import { useState, useEffect, useRef } from 'react'

interface UseToastTimerOptions {
  duration: number
  isPaused: boolean
  onComplete: () => void
}

/**
 * Custom hook to manage toast timer and progress
 * Handles pause/resume and calculates progress percentage
 */
export function useToastTimer({ duration, isPaused, onComplete }: UseToastTimerOptions): number {
  const [progress, setProgress] = useState(0)

  const timerRef = useRef({
    startTime: 0,
    elapsedTime: 0,
  })

  useEffect(() => {
    const isPersistent = !Number.isFinite(duration) || duration <= 0

    if (isPersistent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProgress(0)
      return
    }

    // If paused, we update the total elapsed time and stop.
    if (isPaused) {
      // It's possible to pause before the timer has ever started, so we check if startTime is set.
      if (timerRef.current.startTime > 0) {
        const timeSinceLastStart = Date.now() - timerRef.current.startTime
        timerRef.current.elapsedTime += timeSinceLastStart
        // Reset startTime to indicate the timer is not actively running.
        timerRef.current.startTime = 0
      }
      return
    }

    timerRef.current.startTime = Date.now()

    let animationFrameId: number

    const tick = () => {
      const currentElapsedTime = timerRef.current.elapsedTime + (Date.now() - timerRef.current.startTime)
      const newProgress = Math.floor(Math.min((currentElapsedTime / duration) * 100, 100))

      setProgress(newProgress)

      if (newProgress < 100) {
        animationFrameId = requestAnimationFrame(tick)
      } else {
        onComplete()
      }
    }

    animationFrameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [duration, isPaused, onComplete])

  return progress
}

